import React, { useEffect, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { FileTypeIcon, getFileKind } from "components/common/FileTypeIcon";
import { Card, CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";
import { DateTime, DateOnly } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  AnySourceType,
  SOURCE_TYPE_URL_MAP,
} from "constants/source-type.constants";
import {
  InvoiceSourceType,
  InvoiceSourceTypeLabelMap,
  InvoiceStatus,
  InvoiceStatusMap,
} from "features/Invoices/constants/invoice.constants";
import { ClientInfoCard } from "components/common/ClientInfoCard";
import { Timeline, type TimelineStep, type TimelineStepState } from "components/common/Timeline";
import { useInvoiceOverview } from "./InvoiceOverviewContext";
import { formatCurrency } from "utils/currency";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";
import { getPayments } from "features/Payments/services/PaymentService";
import { Payment } from "features/Payments/types/payment.types";
import {
  PaymentSourceType,
  PaymentStatus,
  paymentStatusMap,
} from "features/Payments/constants/payment.constants";
import { getPaymentExpectations } from "features/PaymentExpectations/services/PaymentExpectationService";
import { PaymentExpectation } from "features/PaymentExpectations/types/payment-expectation.types";
import {
  PaymentExpectationSourceType,
  paymentExpectationStatusMap,
} from "features/PaymentExpectations/constants/payment-expectation.constants";

const OverviewTab = () => {
  const { invoice } = useInvoiceOverview();

  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [expectations, setExpectations] = useState<PaymentExpectation[]>([]);
  const [expectationsLoading, setExpectationsLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const imageAttachments = useMemo(
    () => (invoice?.attachments ?? []).filter((u) => getFileKind(u).isImage),
    [invoice?.attachments],
  );

  useEffect(() => {
    if (!invoice?.id) return;
    let cancelled = false;
    setPaymentsLoading(true);
    getPayments({
      sourceType: PaymentSourceType.INVOICE,
      sourceId: invoice.id,
      limit: 100,
    })
      .then((res) => {
        if (!cancelled) setPayments(res.data?.rows ?? []);
      })
      .catch(() => {
        if (!cancelled) setPayments([]);
      })
      .finally(() => {
        if (!cancelled) setPaymentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoice?.id]);

  useEffect(() => {
    if (!invoice?.id) return;
    let cancelled = false;
    setExpectationsLoading(true);
    getPaymentExpectations({
      sourceType: PaymentExpectationSourceType.INVOICE,
      sourceId: invoice.id,
      limit: 100,
    })
      .then((res) => {
        if (!cancelled) setExpectations(res.data?.rows ?? []);
      })
      .catch(() => {
        if (!cancelled) setExpectations([]);
      })
      .finally(() => {
        if (!cancelled) setExpectationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoice?.id]);

  const totalAmount =
    invoice?.totalAmount != null && Number.isFinite(Number(invoice.totalAmount))
      ? Number(invoice.totalAmount)
      : null;

  const totalPosted = useMemo(
    () =>
      payments
        .filter((p) => p.status === PaymentStatus.POSTED)
        .reduce((acc, p) => acc + Number(p.amount), 0),
    [payments],
  );

  const sourceBase =
    invoice?.sourceType != null
      ? SOURCE_TYPE_URL_MAP[invoice.sourceType as AnySourceType]
      : undefined;

  const invoiceTimeline: TimelineStep[] = invoice
    ? (() => {
        const history = invoice.statusHistory ?? [];
        // No invoice status represents active work happening right now — DRAFT/ISSUED/
        // PARTIALLY_PAID are all "waiting on the client," not an in-progress phase on our side.
        if (history.length === 0) {
          const s = invoice.status as InvoiceStatus;
          const label = InvoiceStatusMap[s]?.label ?? s;
          const state: TimelineStepState =
            s === InvoiceStatus.CANCELLED ? "skipped" : "done";
          return [
            { label, timestamp: invoice.created_at, state, color: InvoiceStatusMap[s]?.color },
          ];
        }
        return history.map((entry) => {
          const s = entry.status as InvoiceStatus;
          const label = InvoiceStatusMap[s]?.label ?? entry.status;
          const state: TimelineStepState =
            s === InvoiceStatus.CANCELLED ? "skipped" : "done";
          const byLabel = entry.user
            ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
            : entry.by != null ? `#${entry.by}` : undefined;
          return {
            label,
            timestamp: entry.at,
            by: byLabel,
            actor: entry.message ?? undefined,
            state,
            color: InvoiceStatusMap[s]?.color,
          };
        });
      })()
    : [];

  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <div className="text-muted">
              <h6 className="mb-3 text-uppercase">Invoice details</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    {invoice?.code ? (
                      <tr>
                        <th
                          className="ps-0"
                          scope="row"
                          style={{ width: "200px" }}
                        >
                          Code :
                        </th>
                        <td className="text-muted">{invoice.code}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <th className="ps-0" scope="row">
                        Status :
                      </th>
                      <td>
                        <StatusBadge
                          value={invoice?.status}
                          map={InvoiceStatusMap}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Source :
                      </th>
                      <td>
                        {invoice?.sourceType != null ? (
                          <div>
                            <div className="text-muted small">
                              {InvoiceSourceTypeLabelMap[
                                invoice.sourceType as InvoiceSourceType
                              ] ?? invoice.sourceType}
                            </div>
                            {sourceBase != null &&
                            invoice?.sourceId != null &&
                            invoice.source?.code ? (
                              <Link
                                to={`${sourceBase}/${invoice.sourceId}`}
                                className="link fw-medium"
                              >
                                {invoice.source.code}
                              </Link>
                            ) : invoice?.source?.code ? (
                              <span className="fw-medium">{invoice.source.code}</span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Client :
                      </th>
                      <td className="text-muted">
                        {invoice?.clientId != null && invoice.client ? (
                          <Link
                            to={`/clients/${invoice.clientId}`}
                            className="link fw-medium"
                          >
                            {(() => {
                              const name = getClientDisplayName(invoice.client);
                              return name !== "—"
                                ? name
                                : invoice.client.email || "—";
                            })()}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Issue date :
                      </th>
                      <td className="text-muted">
                        {invoice?.issueDate ? (
                          <DateOnly date={invoice.issueDate} />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Due date :
                      </th>
                      <td className="text-muted">
                        {invoice?.dueDate ? (
                          <DateOnly date={invoice.dueDate} />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              <div className="pt-3 border-top border-top-dashed mt-4">
                <Row className="gy-3">
                  {invoice?.created_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Created</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={invoice.created_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}

                  {invoice?.updated_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Last updated
                        </p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={invoice.updated_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}

                  {invoice?.deleted_at != null && invoice.deleted_at !== "" ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium text-danger">
                          Deleted
                        </p>
                        <h5 className="fs-14 mb-0 text-danger">
                          <DateTime date={invoice.deleted_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>

        {(invoice?.attachments?.length ?? 0) > 0 ? (
          <>
            <Card>
              <CardBody>
                <h6 className="mb-3 text-uppercase text-muted">
                  Additional files
                </h6>
                <Row className="g-2 row-cols-2 row-cols-lg-5">
                  {invoice?.attachments?.map((url, idx) => {
                    const filename = decodeURIComponent(
                      url.split("/").pop()?.split("?")[0] ?? `File ${idx + 1}`,
                    );
                    const { color, kindLabel, isImage } = getFileKind(url);
                    const BG: Record<string, string> = {
                      success: "bg-success-subtle",
                      danger: "bg-danger-subtle",
                      primary: "bg-primary-subtle",
                      warning: "bg-warning-subtle",
                      info: "bg-info-subtle",
                      secondary: "bg-secondary-subtle",
                    };
                    const imageIdx = isImage ? imageAttachments.indexOf(url) : -1;
                    return (
                      <Col key={idx}>
                        <div className="border rounded p-2 h-100 d-flex flex-column">
                          {isImage ? (
                            <button
                              type="button"
                              className="d-block rounded overflow-hidden bg-light mb-2 border-0 p-0 w-100"
                              style={{ aspectRatio: "4 / 3", cursor: "zoom-in" }}
                              onClick={() => {
                                setLightboxIndex(imageIdx);
                                setLightboxOpen(true);
                              }}
                            >
                              <img
                                src={url}
                                alt=""
                                className="w-100 h-100"
                                style={{ objectFit: "cover" }}
                              />
                            </button>
                          ) : (
                            <div
                              className={`rounded d-flex align-items-center justify-content-center mb-2 ${BG[color] ?? "bg-secondary-subtle"}`}
                              style={{ aspectRatio: "4 / 3", minHeight: "64px" }}
                            >
                              <FileTypeIcon filename={url} style={{ fontSize: "1.75rem" }} />
                            </div>
                          )}
                          <span
                            className="badge bg-body-secondary text-muted border fw-normal mb-1 align-self-start text-uppercase"
                            style={{ fontSize: "0.6rem" }}
                          >
                            {kindLabel}
                          </span>
                          <p
                            className="small fw-medium text-body mb-2 text-break flex-grow-1"
                            title={filename}
                            style={{ fontSize: "0.75rem" }}
                          >
                            {filename}
                          </p>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm w-100 py-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <i className="ri-external-link-line align-middle me-1" />
                            Open
                          </a>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </CardBody>
            </Card>
            {lightboxOpen ? (
              <Lightbox
                open
                close={() => setLightboxOpen(false)}
                index={lightboxIndex}
                slides={imageAttachments.map((src) => ({ src }))}
              />
            ) : null}
          </>
        ) : null}
      </Col>
      <Col lg={4}>
        <Card>
          <CardBody>
            <h6 className="mb-4 text-uppercase text-muted">Timeline</h6>
            <Timeline steps={invoiceTimeline} />

            <h6 className="mb-3 mt-4 text-uppercase text-muted">At a glance</h6>

            <div className="rounded border bg-light bg-opacity-50 p-3">
              {/* Total amount */}
              <div className="d-flex justify-content-between align-items-baseline gap-2 mb-1">
                <span className="text-muted small">Total amount</span>
                <span className="fw-semibold tabular-nums">
                  {totalAmount != null ? (
                    formatCurrency(totalAmount)
                  ) : (
                    <span className="text-muted fs-6 fw-normal">—</span>
                  )}
                </span>
              </div>
              {/* Payments */}
              <div className=" mt-2 pt-2">
                <div className="text-muted small fw-medium mb-2">Payments</div>
                {paymentsLoading ? (
                  <div className="py-2 text-center">
                    <span
                      className="spinner-border spinner-border-sm text-muted"
                      role="status"
                    />
                  </div>
                ) : payments.length === 0 ? (
                  <div className="text-muted small fst-italic py-1">
                    No payments recorded
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {payments.map((p) => {
                      const statusMeta =
                        paymentStatusMap[p.status as PaymentStatus];
                      const isCancelled = p.status === PaymentStatus.CANCELLED;
                      return (
                        <div
                          key={p.id}
                          className={isCancelled ? "opacity-50" : ""}
                        >
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="min-w-0">
                              <span className="small fw-medium text-body text-truncate d-block">
                                {p.code || `#${p.id}`}
                              </span>
                              <span
                                className="text-muted"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {p.paymentDate ? (
                                  <DateOnly date={p.paymentDate} />
                                ) : (
                                  "—"
                                )}
                                {p.modeOfPayment?.name
                                  ? ` · ${p.modeOfPayment.name}`
                                  : null}
                              </span>
                            </div>
                            <div className="text-end flex-shrink-0">
                              <span
                                className={`small fw-semibold tabular-nums d-block ${isCancelled ? "text-decoration-line-through text-muted" : "text-success"}`}
                              >
                                {formatCurrency(p.amount)}
                              </span>
                              <span
                                className={`badge bg-${statusMeta?.color ?? "secondary"}-subtle text-${statusMeta?.color ?? "secondary"}`}
                                style={{ fontSize: "0.65rem" }}
                              >
                                {statusMeta?.label ?? p.status}
                              </span>
                            </div>
                          </div>
                          {p.referenceNumber ? (
                            <div
                              className="text-muted mt-1"
                              style={{ fontSize: "0.72rem" }}
                            >
                              Ref: {p.referenceNumber}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
                {payments.length > 0 ? (
                  <div className="d-flex justify-content-between align-items-baseline gap-2  mt-2 pt-2">
                    <span className="text-muted small">Total received</span>
                    <span className="small fw-semibold tabular-nums text-success">
                      {formatCurrency(totalPosted)}
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Amount due */}
              <div className="border-top mt-2 pt-2 d-flex justify-content-between align-items-baseline gap-2">
                <span className="text-muted small fw-medium">Amount due</span>
                <span className="fs-4 fw-semibold tabular-nums">
                  {formatCurrency(invoice?.outstandingAmount)}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h6 className="mb-3 text-uppercase text-muted">Payment expectations</h6>
            {expectationsLoading ? (
              <div className="py-2 text-center">
                <span className="spinner-border spinner-border-sm text-muted" role="status" />
              </div>
            ) : expectations.length === 0 ? (
              <div className="text-muted small fst-italic py-1">No expectations recorded</div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {expectations.map((exp) => {
                  const statusMeta = paymentExpectationStatusMap[exp.status];
                  return (
                    <div key={exp.id} className="d-flex align-items-start justify-content-between gap-2">
                      <div className="min-w-0">
                        <span className="small fw-medium text-body text-truncate d-block">
                          {exp.code || `#${exp.id}`}
                        </span>
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                          {exp.expectedDate ? <DateOnly date={exp.expectedDate} /> : "—"}
                        </span>
                      </div>
                      <div className="text-end flex-shrink-0">
                        <span className="small fw-semibold tabular-nums d-block">
                          {formatCurrency(exp.expectedAmount)}
                        </span>
                        <span
                          className={`badge bg-${statusMeta?.color ?? "secondary"}-subtle text-${statusMeta?.color ?? "secondary"}`}
                          style={{ fontSize: "0.65rem" }}
                        >
                          {statusMeta?.label ?? exp.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
        {invoice?.client && invoice.clientId ? (
          <ClientInfoCard client={invoice.client} clientId={invoice.clientId} />
        ) : null}
      </Col>
    </Row>
  );
};

export default OverviewTab;
